import React from 'react';
import { ArrowRight } from 'lucide-react';

import type { RegroundDecision } from './api';

/**
 * One prose block the audit considered.
 *
 * The `why` is the finding — "No consumer statement mentions BOGO or Costco" is the report
 * asserting something nobody said — so it is shown open, above the fold, while the full before and
 * after are behind a disclosure. A reviewer scanning ten blocks wants the reasons, not the prose.
 */

const ACTION_STYLES: Record<string, string> = {
  REGROUND: 'bg-blue-50 text-blue-700 border-blue-200',
  CUT: 'bg-red-50 text-red-700 border-red-200',
  DERIVE: 'bg-amber-50 text-amber-800 border-amber-200',
};

const FALLBACK_ACTION_STYLE = 'bg-gray-100 text-gray-600 border-gray-200';

const RegroundDecisionRow: React.FC<{ decision: RegroundDecision }> = ({ decision }) => {
  const actions = decision.actions ?? [];
  const hasProse = !!decision.before || !!decision.after;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11.5px] text-gray-700">
          {decision.path}
        </code>
        {decision.kept === false && (
          <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5
                           text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
            rejected
          </span>
        )}
      </div>

      {decision.reason && (
        <p className="mb-2 text-[12.5px] text-gray-600">{decision.reason}</p>
      )}

      {actions.map((action, index) => (
        <div key={`${action.original ?? ''}-${index}`} className="mb-2 last:mb-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase
                              tracking-wide ${ACTION_STYLES[action.action ?? ''] ?? FALLBACK_ACTION_STYLE}`}>
              {action.action || 'changed'}
            </span>
            {action.why && <span className="text-[12.5px] text-gray-700">{action.why}</span>}
          </div>
          {action.original && (
            <p className="pl-1 text-[12.5px] italic text-gray-500">“{action.original}”</p>
          )}
        </div>
      ))}

      {hasProse && (
        <details className="mt-2 border-t border-gray-100 pt-2">
          <summary className="cursor-pointer text-[11.5px] font-semibold uppercase tracking-wide
                              text-gray-400">
            Full text
          </summary>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 flex items-center gap-1 text-[10.5px] font-bold uppercase
                              tracking-wide text-gray-400">
                As written
              </div>
              <p className="text-[12.5px] text-gray-700">{decision.before}</p>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1 text-[10.5px] font-bold uppercase
                              tracking-wide text-gray-400">
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
                Grounded in what consumers said
              </div>
              <p className="text-[12.5px] text-gray-700">{decision.after}</p>
            </div>
          </div>
        </details>
      )}
    </div>
  );
};

export default RegroundDecisionRow;
